<template>
	<div
		class="match-tags"
		:class="{'is-mobile': isMobileWebApp, '-is-narrow': tagOptions.length > 5 }"
	>
		<slot
			v-if="title !== null"
			name="title"
		>
			<h3 class="section-header">
				{{ title }}
			</h3>
		</slot>
		<div class="match-tags__buttons">
			<template v-for="(tag, index) in tagOptions">
				<TagButton
					:key="`match-tag-buttons-${tag.key}`"
					:icon-name="`tag-${tag.label}`"
					:enabled="isButtonEnabled(tag.tag.name)"
					:active="isButtonActive(tag.tag.name)"
					:highlighted="isButtonHighlighted(tag.tag.name)"
					icon-size="xxl"
					:is-squared="true"
					@click="onTagClick(tag)"
				/>
				<div
					v-if="checkForGapAfter(tag.tag.name) && index !== tagOptions.length - 1"
					:key="`match-tag-button-gap-${tag.key}`"
					class="match-tags__button-gap"
				/>
			</template>
		</div>
	</div>
</template>

<script>
import TagButton from '@videotools/js/one/components/TagButton/TagButton';
import { mapGetters } from 'vuex';
import { playerBus } from '@videotools/js/events';
import {
	computeIntervals,
	getPositionIntervals,
	INTERVAL_TYPE_FIRST_RUN,
	INTERVAL_TYPE_HALFTIME,
	INTERVAL_TYPE_PENALTY,
	INTERVAL_TYPE_RACE,
	INTERVAL_TYPE_SECOND_RUN,
	TAG_FIRST_RUN_START,
	TAG_HALFTIME_END,
	TAG_HALFTIME_START,
	TAG_PENALTY_END,
	TAG_PENALTY_START,
	TAG_RACE_END,
	TAG_RACE_START,
	TAG_SECOND_RUN_START,
	TAG_PLAY_BALL,
	TAG_GAME_BALL,
	TAG_INNING_START,
	TAG_INNING_MID,
	TAG_INNING_END,
	INTERVAL_TYPE_THIRD,
	TAG_THIRD_START,
	TAG_THIRD_END,
	INTERVAL_TYPE_OVERTIME,
	TAG_OVERTIME_START,
	TAG_OVERTIME_END,
	INTERVAL_TYPE_BALL_GAME,
	INTERVAL_TYPE_BALL_GAME_INNING,
	INTERVAL_TYPE_BALL_GAME_PRE_MID,
	INTERVAL_TYPE_BALL_GAME_POST_MID,
} from '@videotools/js/logic/game/event-tag-intervals';

export default {
	name:       'MatchTags',
	components: { TagButton },
	props:      {
		tagOptions: {
			type:    Array,
			default: () => [],
		},
		title: {
			type:    String,
			default: () => null,
		},
	},
	data() {
		return {
			playPosition: 0,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:      'app/isMobileWebApp',
			eventTags:           'event/tags',
			isPlayerCompetition: 'event/isPlayerCompetition',
		}),
		eventTagsSequence() {
			return this.eventTags.filter((eventTag) => {
				return this.tagOptions.some((tagOption) => tagOption.tag.name === eventTag.tag.name && tagOption.tag.type === eventTag.tag.type);
			});
		},
		buttonsStatuses() {
			return this.isPlayerCompetition ? this.buttonStatusesSequentialSport : this.buttonsStatusesConcurrentSport;
		},
		buttonsStatusesConcurrentSport() {
			// compute the button statuses (enabled/disabled & active/inactive)
			// by determining in each interval(s) the play position is
			const playPositionIntervals = getPositionIntervals({
				intervals: this.intervals,
				position:  this.playPosition,
			});

			// ball game phases
			const ballGameStartSet = playPositionIntervals[INTERVAL_TYPE_BALL_GAME].some(interval => interval.start !== null);
			const ballGameEndSet = playPositionIntervals[INTERVAL_TYPE_BALL_GAME].some(interval => interval.end !== null);
			const inningStartSet = playPositionIntervals[INTERVAL_TYPE_BALL_GAME_INNING].some(interval => interval.start !== null);
			const inningEndIsSet = playPositionIntervals[INTERVAL_TYPE_BALL_GAME_INNING].some(interval => interval.end !== null);
			const inningMidPreSet = playPositionIntervals[INTERVAL_TYPE_BALL_GAME_PRE_MID].some(interval => interval.end !== null);
			const inningMidPostSet = playPositionIntervals[INTERVAL_TYPE_BALL_GAME_POST_MID].some(interval => interval.start !== null);

			// ball game start/end controls
			const isStartBallGameEnabled = !ballGameStartSet && !ballGameEndSet;
			const isEndBallGameEnabled = ballGameStartSet && !ballGameEndSet;

			// inning controls
			const isStartInningEnabled = ballGameStartSet && !inningStartSet;
			const isMidInningEnabled = ballGameStartSet && inningStartSet && !inningMidPreSet && !inningMidPostSet;
			const isEndInningEnabled = ballGameStartSet && inningStartSet && !inningEndIsSet;

			// Overtime / Penalty Shootout
			const isInSpecialPhase = playPositionIntervals[INTERVAL_TYPE_OVERTIME].length > 0 || playPositionIntervals[INTERVAL_TYPE_PENALTY].length > 0;

			// Halftime / Thirds
			const isInRegularPhase = playPositionIntervals[INTERVAL_TYPE_HALFTIME].length > 0 || playPositionIntervals[INTERVAL_TYPE_THIRD].length > 0;

			const isHtStartEnabled = !isInRegularPhase && !isInSpecialPhase && !playPositionIntervals[INTERVAL_TYPE_HALFTIME].some(interval => interval.start !== null);
			const isHtEndEnabled = playPositionIntervals[INTERVAL_TYPE_HALFTIME].some(interval => interval.end === null);

			const isPenStartEnabled = !isInRegularPhase && !isInSpecialPhase && !playPositionIntervals[INTERVAL_TYPE_PENALTY].some(interval => interval.start !== null);
			const isPenEndEnabled = playPositionIntervals[INTERVAL_TYPE_PENALTY].some(interval => interval.end === null);

			// Icehockey rules (thirds instead of halftimes, overtime after last third)
			const isThirdStartEnabled = !isInRegularPhase && !isInSpecialPhase && !playPositionIntervals[INTERVAL_TYPE_THIRD].some(interval => interval.start !== null);
			const isThirdEndEnabled = playPositionIntervals[INTERVAL_TYPE_THIRD].some(interval => interval.end === null);

			const isOvertimeStartEnabled = !isInRegularPhase && !isInSpecialPhase && !playPositionIntervals[INTERVAL_TYPE_OVERTIME].some(interval => interval.start !== null);
			const isOvertimeEndEnabled = playPositionIntervals[INTERVAL_TYPE_OVERTIME].some(interval => interval.end === null);

			return {
				[TAG_PLAY_BALL]: {
					enabled:     isStartBallGameEnabled,
					active:      false,
					highlighted: isStartBallGameEnabled,
					class:       'gap--large',
					gapAfter:    true,
				},
				[TAG_INNING_START]: {
					enabled:     isStartInningEnabled,
					active:      false,
					highlighted: isStartInningEnabled,
					class:       'gap--small',
					gapAfter:    false,
				},
				[TAG_INNING_MID]: {
					enabled:     isMidInningEnabled,
					active:      isMidInningEnabled,
					highlighted: false,
					class:       'gap--small',
					gapAfter:    false,
				},
				[TAG_INNING_END]: {
					enabled:     isEndInningEnabled,
					active:      false,
					highlighted: isEndInningEnabled,
					class:       'gap--large',
					gapAfter:    true,
				},
				[TAG_GAME_BALL]: {
					enabled:     isEndBallGameEnabled,
					active:      false,
					highlighted: isEndBallGameEnabled,
					class:       'gap--small',
					gapAfter:    false,
				},
				[TAG_HALFTIME_START]: {
					enabled:     isHtStartEnabled,
					active:      false,
					highlighted: isHtStartEnabled,
				},
				[TAG_HALFTIME_END]: {
					enabled:     isHtEndEnabled,
					active:      false, // no rounded border for the end tag
					highlighted: isHtEndEnabled,
					gapAfter:    true,
				},
				[TAG_PENALTY_START]: {
					enabled:     isPenStartEnabled,
					active:      !isPenStartEnabled && isPenEndEnabled,
					highlighted: false,
				},
				[TAG_PENALTY_END]: {
					enabled:     isPenEndEnabled,
					active:      false,
					highlighted: isPenEndEnabled,
					gapAfter:    true,
				},
				[TAG_THIRD_START]: {
					enabled:     isThirdStartEnabled,
					active:      false,
					highlighted: isThirdStartEnabled,
				},
				[TAG_THIRD_END]: {
					enabled:     isThirdEndEnabled,
					active:      false,
					highlighted: isThirdEndEnabled,
					gapAfter:    true,
				},
				[TAG_OVERTIME_START]: {
					enabled:     isOvertimeStartEnabled,
					active:      false,
					highlighted: false,
				},
				[TAG_OVERTIME_END]: {
					enabled:     isOvertimeEndEnabled,
					active:      false,
					highlighted: isOvertimeEndEnabled,
					gapAfter:    true,
				},
			};
		},
		buttonStatusesSequentialSport() {
			// compute the button statuses (enabled/disabled & active/inactive)
			// by determining in each interval(s) the play position is
			const playPositionIntervals = getPositionIntervals({
				intervals: this.intervals,
				position:  this.playPosition,
			});

			// we assume there will be only one race per event
			const isInRace = playPositionIntervals[INTERVAL_TYPE_RACE].length > 0;
			const hasFirstRun = this.intervals[INTERVAL_TYPE_FIRST_RUN].length > 0;
			const hasSecondRun = this.intervals[INTERVAL_TYPE_SECOND_RUN].some((interval) => interval.start !== null);

			const isRaceStartEnabled = !playPositionIntervals[INTERVAL_TYPE_RACE].some((interval) => interval.start !== null);
			const isRaceEndEnabled = playPositionIntervals[INTERVAL_TYPE_RACE].some((interval) => interval.end === null);

			// first run is enabled only in race & only if there wasn't any first run tag already added
			const isFirstRunEnabled = isInRace && !hasFirstRun;
			const isFirstRunActive = isInRace && playPositionIntervals[INTERVAL_TYPE_FIRST_RUN].length > 0;

			// second run is enabled only if:
			// 	1) in race
			//  2) first run has been set
			//	3) second run hasn't been set yet
			//	4) play position is after first run
			const isSecondRunEnabled = isInRace &&
				hasFirstRun &&
				!hasSecondRun &&
				!!playPositionIntervals[INTERVAL_TYPE_FIRST_RUN].length;
			const isSecondRunActive = isInRace && hasSecondRun && !!playPositionIntervals[INTERVAL_TYPE_SECOND_RUN].length;

			return {
				[TAG_RACE_START]: {
					enabled:     isRaceStartEnabled,
					active:      isInRace && !isRaceStartEnabled && isRaceEndEnabled,
					highlighted: isRaceStartEnabled,
				},
				[TAG_RACE_END]: {
					enabled:     isRaceEndEnabled,
					active:      false, // no rounded border for the end tag
					highlighted: isRaceEndEnabled,
				},
				[TAG_FIRST_RUN_START]: {
					enabled:     isFirstRunEnabled,
					active:      isFirstRunActive,
					highlighted: false,
				},
				[TAG_SECOND_RUN_START]: {
					enabled:     isSecondRunEnabled,
					active:      isSecondRunActive,
					highlighted: isSecondRunEnabled,
				},
			};
		},
		intervals() {
			return computeIntervals({ eventTagsSequence: this.eventTagsSequence });
		},
	},
	mounted() {
		playerBus.subscribePosition(this.handlePositionUpdate);
	},
	destroyed() {
		playerBus.unsubscribePosition(this.handlePositionUpdate);
	},
	methods: {
		onTagClick(tag) {
			this.$emit('tag-click', tag);
		},
		checkForGapAfter(tagName) {
			return this.buttonsStatuses[tagName]?.gapAfter;
		},
		isButtonEnabled(tagName) {
			if (!this.buttonsStatuses[tagName]) {
				// enabled by default
				return true;
			}
			return this.buttonsStatuses[tagName].enabled;
		},
		isButtonActive(tagName) {
			if (!this.buttonsStatuses[tagName]) {
				// inactive by default
				return false;
			}
			return this.buttonsStatuses[tagName].active;
		},
		isButtonHighlighted(tagName) {
			if (!this.buttonsStatuses[tagName]) {
				// inactive by default
				return false;
			}
			return this.buttonsStatuses[tagName].highlighted;
		},
		handlePositionUpdate(value) {
			this.playPosition = value;
		},
	},
};
</script>

<style lang="scss" scoped>
// .match-tags {}

.match-tags__buttons {
	display: flex;
	flex-wrap: wrap;
	gap: 3px;
}

.match-tags__button-gap {
	flex-grow: 1;
}
</style>
